import { useCallback, useId, useRef, useState, type ReactNode } from "react";
import {
  BellRing,
  BookOpenText,
  Boxes,
  BrainCircuit,
  Calculator,
  CalendarDays,
  History,
  Layers3,
  NotebookPen,
  ScanLine,
} from "lucide-react";
import { motion, useReducedMotion } from "motion/react";
import { useExtracted } from "next-intl";
import { cn } from "@/lib/utils";
import { useMarketingSignup } from "@/hooks/useMarketingSignup";
import { EmailActionField } from "@/components/auth/EmailActionField";
import Link from "@/components/compat/Link";
import { LocaleToggleUnauth } from "@/components/layout/LocaleToggleUnauth";
import { UnauthShell } from "@/components/layout/UnauthShell";
import { MARKETING_EASE } from "@/components/marketing/MarketingPagePrimitives";
import { Button } from "@/components/ui/button";
import { FoilButton } from "@/components/ui/foil-button";
import { LogoIcon } from "@/components/ui/logo-icon";
import { Logotype } from "@/components/ui/logotype";

type Accent = "cyan" | "gold" | "purple";

const ACCENT_CLASSES: Record<
  Accent,
  { border: string; glow: string; text: string }
> = {
  cyan: {
    border: "from-cyan-100/60 via-cyan-300/20 to-violet-300/42",
    glow: "bg-cyan-300/12",
    text: "text-cyan-100",
  },
  gold: {
    border: "from-cyan-100/54 via-violet-200/24 to-fuchsia-200/44",
    glow: "bg-violet-300/10",
    text: "text-violet-100",
  },
  purple: {
    border: "from-violet-100/64 via-fuchsia-300/18 to-cyan-200/44",
    glow: "bg-violet-300/12",
    text: "text-violet-100",
  },
};

function NeonFrame({
  children,
  className,
  accent = "cyan",
  clear = false,
}: {
  children: ReactNode;
  className?: string;
  accent?: Accent;
  clear?: boolean;
}) {
  const tone = ACCENT_CLASSES[accent];

  return (
    <div
      className={cn(
        "group relative rounded-[24px] sm:rounded-[30px]",
        clear
          ? "border border-cyan-100/24 bg-transparent p-0"
          : "bg-gradient-to-br p-px",
        tone.border,
        className,
      )}
    >
      <div
        className={cn(
          "pointer-events-none absolute -inset-8 rounded-[42px] opacity-55 blur-3xl transition-opacity duration-500 group-hover:opacity-85",
          clear && "hidden",
          tone.glow,
        )}
      />
      <div
        className={cn(
          "relative h-full overflow-hidden rounded-[23px] border sm:rounded-[29px]",
          clear
            ? "border-white/8 bg-transparent shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]"
            : "border-white/[0.055] bg-[linear-gradient(145deg,rgba(13,24,34,0.76),rgba(5,11,18,0.5)_58%,rgba(15,12,27,0.68))] shadow-[inset_0_1px_0_rgba(255,255,255,0.16),inset_0_-1px_0_rgba(255,255,255,0.035),0_34px_100px_rgba(0,0,0,0.42)] backdrop-blur-xl",
        )}
      >
        <div
          className={cn(
            "pointer-events-none absolute inset-0",
            clear
              ? "hidden"
              : "bg-[linear-gradient(115deg,rgba(255,255,255,0.075),transparent_22%,transparent_72%,rgba(142,116,255,0.045))]",
          )}
        />
        <div className="pointer-events-none absolute inset-x-[12%] top-0 h-px bg-[linear-gradient(90deg,transparent,rgba(226,250,255,0.9),transparent)] opacity-70 shadow-[0_0_14px_rgba(130,230,255,0.55)]" />
        <div className="pointer-events-none absolute top-4 left-4 size-3 border-t border-l border-white/36" />
        <div className="pointer-events-none absolute right-4 bottom-4 size-3 border-r border-b border-white/24" />
        {children}
      </div>
    </div>
  );
}

function Eyebrow({
  children,
  accent = "cyan",
}: {
  children: ReactNode;
  accent?: Accent;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-2 text-[10px] font-semibold tracking-[0.24em] uppercase",
        ACCENT_CLASSES[accent].text,
      )}
    >
      <span className="h-px w-6 bg-current opacity-55" />
      {children}
    </span>
  );
}

function ReviewVisual({
  compact = false,
  labels,
}: {
  compact?: boolean;
  labels: {
    title: string;
    period: string;
    performance: string;
    drawdown: string;
    attribution: string;
    reminders: string;
    review: string;
    expiry: string;
    note: string;
  };
}) {
  const lineGradientId = useId();
  const fillGradientId = useId();

  return (
    <NeonFrame
      accent="gold"
      clear
      className={cn(
        compact
          ? "min-h-[350px] sm:min-h-[390px]"
          : "min-h-[420px] sm:min-h-[500px]",
      )}
    >
      <div
        className={cn(
          "relative flex h-full flex-col p-5",
          compact
            ? "min-h-[350px] sm:min-h-[390px] sm:p-6"
            : "min-h-[420px] sm:min-h-[500px] sm:p-7",
        )}
      >
        <div
          className={cn(
            "flex items-center justify-between border-b border-white/8",
            compact ? "pb-3" : "pb-4",
          )}
        >
          <div>
            <div className="text-[10px] tracking-[0.2em] text-violet-100/64 uppercase">
              {labels.review}
            </div>
            <div className="mt-1 text-lg font-semibold text-white/92">
              {labels.title}
            </div>
          </div>
          <span className="rounded-full border border-violet-100/18 bg-violet-100/[0.055] px-3 py-1 text-[10px] text-violet-50/72">
            {labels.period}
          </span>
        </div>

        <div
          className={cn("grid grid-cols-3 gap-2.5", compact ? "mt-4" : "mt-5")}
        >
          {[
            [labels.performance, "+8.4%", "text-emerald-100"],
            [labels.drawdown, "−2.1%", "text-rose-100"],
            [labels.attribution, "67%", "text-cyan-100"],
          ].map(([label, value, tone]) => (
            <div
              className={cn(
                "rounded-2xl border border-white/9 bg-white/[0.045] px-3",
                compact ? "py-2.5" : "py-3",
              )}
              key={label}
            >
              <div className="text-[9px] tracking-[0.12em] text-white/36 uppercase">
                {label}
              </div>
              <div className={cn("font-mono-numbers mt-2 text-lg", tone)}>
                {value}
              </div>
            </div>
          ))}
        </div>

        <div
          className={cn(
            "relative flex-1 overflow-hidden rounded-2xl border border-white/8 bg-black/24 p-3",
            compact ? "mt-4 min-h-28" : "mt-5 min-h-38",
          )}
        >
          <div className="absolute inset-0 opacity-45 [background-image:linear-gradient(rgba(255,255,255,0.055)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.04)_1px,transparent_1px)] [background-size:100%_32px,44px_100%]" />
          <svg
            aria-hidden
            className={cn(
              "relative h-full w-full",
              compact ? "min-h-24" : "min-h-32",
            )}
            preserveAspectRatio="none"
            viewBox="0 0 520 170"
          >
            <defs>
              <linearGradient id={lineGradientId} x1="0" x2="1">
                <stop offset="0" stopColor="#d9c8ff" />
                <stop offset="0.48" stopColor="#bff5ff" />
                <stop offset="1" stopColor="#b6a7ff" />
              </linearGradient>
              <linearGradient id={fillGradientId} x1="0" x2="0" y1="0" y2="1">
                <stop offset="0" stopColor="#bff5ff" stopOpacity="0.2" />
                <stop offset="1" stopColor="#bff5ff" stopOpacity="0" />
              </linearGradient>
            </defs>
            <path
              d="M0 146 C45 139 57 112 96 118 C136 124 157 84 197 91 C239 99 263 57 304 67 C348 77 371 35 414 43 C456 51 478 15 520 23 L520 170 L0 170 Z"
              fill={`url(#${fillGradientId})`}
            />
            <path
              d="M0 146 C45 139 57 112 96 118 C136 124 157 84 197 91 C239 99 263 57 304 67 C348 77 371 35 414 43 C456 51 478 15 520 23"
              fill="none"
              stroke={`url(#${lineGradientId})`}
              strokeWidth="2.25"
            />
            {[96, 197, 304, 414].map((x, index) => (
              <circle
                cx={x}
                cy={[118, 91, 67, 43][index]}
                fill="#08131d"
                key={x}
                r="4.5"
                stroke={index % 2 === 0 ? "#d9c8ff" : "#bff5ff"}
                strokeWidth="2"
              />
            ))}
          </svg>
        </div>

        <div
          className={cn("grid gap-2 sm:grid-cols-3", compact ? "mt-3" : "mt-4")}
        >
          {[
            [BellRing, labels.reminders],
            [History, labels.expiry],
            [NotebookPen, labels.note],
          ].map(([Icon, label]) => {
            const ItemIcon = Icon as typeof BellRing;
            return (
              <div
                className="flex items-center gap-2 rounded-full border border-white/8 bg-white/[0.025] px-3 py-2 text-[10px] text-white/52"
                key={label as string}
              >
                <ItemIcon className="text-violet-100/66" size={12} />
                <span className="truncate">{label as string}</span>
              </div>
            );
          })}
        </div>
      </div>
    </NeonFrame>
  );
}

function CalendarVisual({
  labels,
}: {
  labels: {
    title: string;
    macro: string;
    expiry: string;
    reminder: string;
    replay: string;
    month: string;
    reminderMeta: string;
    replayMeta: string;
  };
}) {
  const days = Array.from({ length: 28 }, (_, index) => index + 1);
  return (
    <NeonFrame clear className="min-h-[390px]">
      <div className="relative p-5 sm:p-7">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="flex size-9 items-center justify-center rounded-full border border-cyan-100/18 bg-cyan-100/[0.045] text-cyan-100">
              <CalendarDays size={16} />
            </span>
            <div>
              <div className="text-[10px] tracking-[0.2em] text-cyan-100/55 uppercase">
                {labels.title}
              </div>
              <div className="mt-1 text-sm font-semibold text-white/88">
                {labels.month}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            {[labels.macro, labels.expiry].map((label, index) => (
              <span
                className={cn(
                  "rounded-full border px-2 py-1 text-[9px]",
                  index === 0
                    ? "border-violet-200/18 bg-violet-200/[0.055] text-violet-100/72"
                    : "border-amber-100/18 bg-amber-100/[0.05] text-amber-100/70",
                )}
                key={label}
              >
                {label}
              </span>
            ))}
          </div>
        </div>

        <div className="mt-5 grid grid-cols-7 gap-1.5">
          {days.map((day) => {
            const event = [4, 9, 14, 18, 21, 27].includes(day);
            const selected = day === 18;
            return (
              <div
                className={cn(
                  "relative flex aspect-square items-center justify-center rounded-xl border text-[11px]",
                  selected
                    ? "border-cyan-100/46 bg-cyan-100/10 text-cyan-50 shadow-[0_0_18px_rgba(116,229,255,0.12)]"
                    : "border-white/[0.055] bg-white/[0.022] text-white/44",
                )}
                key={day}
              >
                {day}
                {event ? (
                  <span
                    className={cn(
                      "absolute bottom-1 size-1 rounded-full",
                      day % 2 === 0 ? "bg-violet-200" : "bg-amber-100",
                    )}
                  />
                ) : null}
              </div>
            );
          })}
        </div>

        <div className="mt-4 grid gap-2 sm:grid-cols-2">
          <div className="flex items-center gap-3 rounded-2xl border border-cyan-100/12 bg-cyan-100/[0.035] p-3">
            <BellRing className="text-cyan-100/72" size={15} />
            <div>
              <div className="text-[10px] text-white/72">{labels.reminder}</div>
              <div className="mt-0.5 text-[9px] text-white/34">
                {labels.reminderMeta}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3 rounded-2xl border border-violet-100/12 bg-violet-100/[0.035] p-3">
            <History className="text-violet-100/72" size={15} />
            <div>
              <div className="text-[10px] text-white/72">{labels.replay}</div>
              <div className="mt-0.5 text-[9px] text-white/34">
                {labels.replayMeta}
              </div>
            </div>
          </div>
        </div>
      </div>
    </NeonFrame>
  );
}

function StructureVisual({
  labels,
}: {
  labels: {
    title: string;
    price: string;
    time: string;
    volatility: string;
    output: string;
  };
}) {
  return (
    <NeonFrame accent="purple" className="min-h-[410px]">
      <div className="relative min-h-[410px] overflow-hidden">
        <img
          alt={labels.title}
          className="absolute inset-0 h-full w-full object-cover opacity-72 saturate-[0.82]"
          height={900}
          loading="lazy"
          src="/screenshots/marketing/manifold-gamma.jpg"
          width={695}
        />
        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(4,8,16,0.06),rgba(4,8,16,0.78)_72%,rgba(4,8,16,0.96))]" />
        <div className="relative flex min-h-[410px] flex-col justify-between p-5 sm:p-7">
          <div className="flex items-center justify-between">
            <div className="rounded-full border border-violet-100/22 bg-black/32 px-3 py-1.5 text-[10px] tracking-[0.18em] text-violet-50/82 uppercase backdrop-blur-md">
              {labels.title}
            </div>
            <ScanLine className="text-cyan-100/70" size={17} />
          </div>
          <div className="grid grid-cols-2 gap-2.5">
            {[
              [labels.price, "X"],
              [labels.time, "Y"],
              [labels.volatility, "λ"],
              [labels.output, "Z"],
            ].map(([label, axis], index) => (
              <div
                className={cn(
                  "rounded-2xl border px-3 py-3 backdrop-blur-md",
                  index === 0
                    ? "border-cyan-100/24 bg-cyan-100/[0.065]"
                    : index === 1
                      ? "border-violet-100/24 bg-violet-100/[0.065]"
                      : index === 2
                        ? "border-amber-100/22 bg-amber-100/[0.055]"
                        : "border-fuchsia-100/22 bg-fuchsia-100/[0.055]",
                )}
                key={label}
              >
                <span className="font-mono-numbers text-[10px] text-white/32">
                  {axis}
                </span>
                <div className="mt-1 text-xs text-white/78">{label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </NeonFrame>
  );
}

function KnowledgeVisual({
  labels,
}: {
  labels: {
    title: string;
    theory: string;
    library: string;
    compound: string;
    guidance: string;
  };
}) {
  return (
    <NeonFrame clear className="min-h-[400px]">
      <div className="relative min-h-[400px] p-5 sm:p-7">
        <div className="flex items-center gap-3">
          <span className="flex size-9 items-center justify-center rounded-full border border-cyan-100/18 bg-cyan-100/[0.045] text-cyan-100">
            <BookOpenText size={16} />
          </span>
          <div>
            <div className="text-[10px] tracking-[0.2em] text-cyan-100/55 uppercase">
              {labels.title}
            </div>
            <div className="mt-1 text-sm font-semibold text-white/88">
              Δ · Γ · Θ · ν
            </div>
          </div>
        </div>

        <div className="relative mt-5 h-40 overflow-hidden rounded-2xl border border-white/8 bg-black/18">
          <div className="absolute inset-0 opacity-38 [background-image:linear-gradient(rgba(255,255,255,0.06)_1px,transparent_1px)] [background-size:100%_28px]" />
          <svg
            aria-hidden
            className="absolute inset-0 h-full w-full"
            preserveAspectRatio="none"
            viewBox="0 0 500 160"
          >
            <path
              d="M0 130 C50 124 55 52 110 58 C162 64 175 132 230 121 C286 110 290 32 344 38 C405 44 425 119 500 92"
              fill="none"
              stroke="#bff5ff"
              strokeOpacity="0.82"
              strokeWidth="2"
            />
            <path
              d="M0 114 C70 105 86 83 140 88 C210 95 218 43 286 51 C357 60 392 83 500 28"
              fill="none"
              stroke="#d7c4ff"
              strokeDasharray="5 5"
              strokeOpacity="0.52"
              strokeWidth="1.5"
            />
          </svg>
          <div className="absolute top-4 left-4 font-mono text-[11px] text-white/42">
            ∂V/∂t · ∂²V/∂S² · σ
          </div>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-2.5">
          {[
            [BookOpenText, labels.theory],
            [NotebookPen, labels.library],
            [Calculator, labels.compound],
            [BrainCircuit, labels.guidance],
          ].map(([Icon, label]) => {
            const ItemIcon = Icon as typeof BookOpenText;
            return (
              <div
                className="flex items-center gap-2.5 rounded-2xl border border-white/8 bg-white/[0.026] px-3 py-3 text-[10px] text-white/58"
                key={label as string}
              >
                <ItemIcon className="text-cyan-100/66" size={14} />
                {label as string}
              </div>
            );
          })}
        </div>
      </div>
    </NeonFrame>
  );
}

function FeatureSection({
  accent,
  bullets,
  children,
  description,
  eyebrow,
  reverse = false,
  title,
}: {
  accent: Accent;
  bullets: Array<{ icon: typeof Layers3; label: string }>;
  children: ReactNode;
  description: string;
  eyebrow: string;
  reverse?: boolean;
  title: string;
}) {
  return (
    <motion.section
      className="grid items-center gap-10 py-16 lg:grid-cols-[minmax(0,0.82fr)_minmax(0,1.18fr)] lg:gap-16 lg:py-24"
      initial={{ opacity: 0, y: 24 }}
      transition={{ duration: 0.7, ease: MARKETING_EASE }}
      viewport={{ once: true, margin: "-80px" }}
      whileInView={{ opacity: 1, y: 0 }}
    >
      <div className={cn("max-w-lg", reverse && "lg:order-2")}>
        <Eyebrow accent={accent}>{eyebrow}</Eyebrow>
        <h2 className="font-heading mt-5 text-3xl leading-[1.08] font-semibold tracking-[-0.04em] text-white sm:text-4xl">
          {title}
        </h2>
        <p className="mt-5 text-sm leading-7 text-white/54 sm:text-base">
          {description}
        </p>
        <div className="mt-7 space-y-3">
          {bullets.map(({ icon: Icon, label }) => (
            <div
              className="flex items-center gap-3 text-sm text-white/68"
              key={label}
            >
              <span
                className={cn(
                  "flex size-8 shrink-0 items-center justify-center rounded-full border border-white/9 bg-white/[0.03]",
                  ACCENT_CLASSES[accent].text,
                )}
              >
                <Icon size={14} />
              </span>
              <span>{label}</span>
            </div>
          ))}
        </div>
      </div>
      <div className={cn(reverse && "lg:order-1")}>{children}</div>
    </motion.section>
  );
}

export function LandingPage() {
  const te = useExtracted();
  const reduceMotion = useReducedMotion();
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState(false);
  const [emailErrorMsg, setEmailErrorMsg] = useState("");
  const [signupSent, setSignupSent] = useState(false);
  const featureStartRef = useRef<HTMLDivElement>(null);
  const { loading, signup, challenge } = useMarketingSignup();

  const shakeEmail = useCallback((message: string) => {
    setEmailError(false);
    setEmailErrorMsg(message);
    requestAnimationFrame(() => setEmailError(true));
  }, []);

  const handleSignup = useCallback(async () => {
    if (!email || !email.includes("@")) {
      shakeEmail(te("Enter your email to continue"));
      return;
    }
    const result = await signup(email);
    if (result === "success") {
      setSignupSent(true);
      return;
    }
    shakeEmail(
      result === "email_taken"
        ? te("This email already has an account")
        : te("Something went wrong. Please try again."),
    );
  }, [email, shakeEmail, signup, te]);

  const planBullets = [
    {
      icon: CalendarDays,
      label: te("A market calendar built for trading plans"),
    },
    { icon: BellRing, label: te("Events, reminders, and attention points") },
    { icon: History, label: te("Replay a past market window in context") },
  ];
  const structureBullets = [
    { icon: Layers3, label: te("Layered multi-leg strategy modeling") },
    { icon: Boxes, label: te("Price, time, volatility, and P&L in 3D") },
    { icon: ScanLine, label: te("Payoff, Greeks, and volatility surfaces") },
  ];
  const knowledgeBullets = [
    {
      icon: BookOpenText,
      label: te("Options theory and strategy recognition"),
    },
    {
      icon: NotebookPen,
      label: te("A personal knowledge base for notes and reviews"),
    },
    {
      icon: Calculator,
      label: te("Compound scenarios and context-aware explanations"),
    },
  ];

  return (
    <UnauthShell hideHeader mode="scroll">
      <main
        className="relative mx-auto w-full max-w-[78rem] px-5 pb-24 sm:px-8"
        id="top"
      >
        <section className="relative grid min-h-dvh items-center gap-10 py-10 sm:py-14 lg:grid-cols-[minmax(0,0.86fr)_minmax(28rem,1.14fr)] lg:gap-14 lg:py-16">
          <nav
            aria-label={te("Main navigation")}
            className="absolute top-5 right-0 z-10 flex items-center gap-5 sm:top-7 lg:top-9"
          >
            <LocaleToggleUnauth />
          </nav>

          <motion.div
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl"
            initial={{ opacity: 0, y: reduceMotion ? 0 : 24 }}
            transition={{ duration: 0.7, ease: MARKETING_EASE }}
          >
            <div className="flex items-center gap-3 pr-14">
              <LogoIcon className="logo-foil-soft" foil size="md" />
              <Logotype glow size="md" />
            </div>

            <div className="mt-9">
              <Eyebrow accent="gold">{te("Review workspace")}</Eyebrow>
            </div>
            <h1 className="font-heading mt-5 text-4xl leading-[1.01] font-semibold tracking-[-0.052em] text-balance text-white sm:text-5xl lg:text-[3.65rem] xl:text-[4.35rem]">
              {te("Make every decision reviewable.")}
            </h1>
            <p className="mt-6 max-w-xl text-base leading-7 text-white/62 xl:text-lg xl:leading-8">
              {te(
                "Learn options, simulate strategies, and record and review your trading decisions in one workspace.",
              )}
            </p>
            <p className="mt-3 max-w-xl text-sm leading-6 text-white/40">
              {te(
                "Built for reflection and risk understanding — not trade calls, execution, or investment advice.",
              )}
            </p>

            <div className="mt-8 w-full max-w-xl">
              {signupSent ? (
                <NeonFrame accent="gold">
                  <div className="flex items-center justify-center gap-4 p-5 text-left">
                    <span className="flex size-10 items-center justify-center rounded-full border border-emerald-100/22 bg-emerald-100/[0.06] text-emerald-100">
                      ✓
                    </span>
                    <div>
                      <div className="text-sm font-medium text-white/88">
                        {te("Check your email for your PIN")}
                      </div>
                      <Link
                        className="mt-1 inline-block text-xs text-cyan-100/66 hover:text-cyan-50"
                        href="/login"
                      >
                        {te("Go to Login")}
                      </Link>
                    </div>
                  </div>
                </NeonFrame>
              ) : (
                <div>
                  <EmailActionField
                    action={
                      loading ? te("Sending...") : te("Start with 2.0 for free")
                    }
                    animateError
                    disabled={loading}
                    error={emailError}
                    id="marketing-email"
                    label={te("email")}
                    placeholder={te("you@email.com")}
                    value={email}
                    onSubmit={() => void handleSignup()}
                    onValueChange={(nextEmail) => {
                      setEmail(nextEmail);
                      if (emailError) setEmailError(false);
                    }}
                  />
                  {challenge}
                  <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 px-2">
                    <span
                      className={cn(
                        "text-[11px]",
                        emailError ? "text-rose-200/76" : "text-white/42",
                      )}
                    >
                      {emailError
                        ? emailErrorMsg
                        : te("No card required. No trial countdown.")}
                    </span>
                    <Link
                      className="text-sm font-medium text-cyan-100/88 transition-colors hover:text-cyan-50"
                      href="/purchase"
                    >
                      {te("View all plans")}
                    </Link>
                    <Link
                      className="text-sm font-medium text-violet-100/82 transition-colors hover:text-violet-50"
                      href="/login"
                    >
                      {te("Log in")}
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </motion.div>

          <motion.div
            animate={{ opacity: 1, scale: 1, y: 0 }}
            initial={{
              opacity: 0,
              scale: reduceMotion ? 1 : 0.96,
              y: reduceMotion ? 0 : 18,
            }}
            transition={{
              duration: 0.8,
              ease: MARKETING_EASE,
              delay: 0.12,
            }}
          >
            <ReviewVisual
              compact
              labels={{
                attribution: te("Attribution"),
                drawdown: te("Drawdown"),
                expiry: te("Expiry timeline"),
                note: te("Decision notes"),
                performance: te("Performance"),
                period: te("This quarter"),
                reminders: te("Reminders"),
                review: te("Review center"),
                title: te("Position and decision review"),
              }}
            />
          </motion.div>
        </section>

        <div ref={featureStartRef}>
          <FeatureSection
            reverse
            accent="cyan"
            bullets={planBullets}
            description={te(
              "Bring market events, expiries, personal reminders, and historical context into one timeline without turning the calendar into a trade signal.",
            )}
            eyebrow={te("Market planning")}
            title={te("Keep market time, events, and reminders in one place.")}
          >
            <CalendarVisual
              labels={{
                expiry: te("Expiry"),
                macro: te("Macro"),
                month: te("August 2026"),
                reminder: te("Review before the event"),
                reminderMeta: te("SPX · 08:30 ET"),
                replay: te("Open market replay"),
                replayMeta: te("SPX · 2025–2026"),
                title: te("Market calendar"),
              }}
            />
          </FeatureSection>
        </div>

        <FeatureSection
          accent="purple"
          bullets={structureBullets}
          description={te(
            "Build layered option structures, read payoff and Greeks, then move through price, time, and volatility to understand how the risk changes.",
          )}
          eyebrow={te("Structure and dimensions")}
          title={te("Model the structure before capital is at risk.")}
        >
          <StructureVisual
            labels={{
              output: te("P&L and Greeks"),
              price: te("Price"),
              time: te("Time to expiry"),
              title: te("Multi-dimensional model"),
              volatility: te("Volatility"),
            }}
          />
          <Link
            className="mt-4 inline-flex rounded-full border border-cyan-200/25 bg-cyan-300/10 px-5 py-2.5 text-sm text-cyan-100 transition hover:bg-cyan-300/20"
            href="/manifold"
          >
            {te("Explore options in 3D")}
          </Link>
        </FeatureSection>

        <FeatureSection
          reverse
          accent="cyan"
          bullets={knowledgeBullets}
          description={te(
            "Study theory, recognize structures, save review notes, test compound scenarios, and turn explanations into a personal knowledge system.",
          )}
          eyebrow={te("Learning loop")}
          title={te("Turn every review into reusable knowledge.")}
        >
          <KnowledgeVisual
            labels={{
              compound: te("Compound calculator"),
              guidance: te("Context-aware guidance"),
              library: te("Knowledge base and notes"),
              theory: te("Options theory"),
              title: te("Knowledge workspace"),
            }}
          />
        </FeatureSection>

        <motion.section
          className="py-16 lg:py-24"
          initial={{ opacity: 0, y: 22 }}
          transition={{ duration: 0.7, ease: MARKETING_EASE }}
          viewport={{ once: true, margin: "-80px" }}
          whileInView={{ opacity: 1, y: 0 }}
        >
          <NeonFrame accent="purple">
            <div className="relative grid gap-8 p-7 sm:p-10 lg:grid-cols-[1fr_auto] lg:items-center lg:p-12">
              <div>
                <Eyebrow accent="purple">{te("Scope and safety")}</Eyebrow>
                <h2 className="font-heading mt-5 text-3xl font-semibold tracking-[-0.04em] text-white">
                  {te("Built for reflection, not trade calls.")}
                </h2>
                <p className="mt-4 max-w-2xl text-sm leading-7 text-white/50">
                  {te(
                    "ABG does not route orders, hold funds, or tell you what to buy or sell. It gives you tools to document, model, understand, and review your own decisions.",
                  )}
                </p>
              </div>
              <Link href="/safety">
                <Button
                  className="w-full border-transparent bg-black text-white/88 hover:bg-black hover:text-white lg:w-auto"
                  variant="outline"
                >
                  {te("Read scope and safety")}
                </Button>
              </Link>
            </div>
          </NeonFrame>
        </motion.section>

        <section className="py-16 lg:py-24" id="pricing">
          <div className="mx-auto max-w-3xl text-center">
            <div className="text-[9px] font-semibold tracking-[0.28em] text-violet-100/72 uppercase sm:text-[10px]">
              {te("Membership")}
            </div>
            <h2 className="font-heading mt-5 text-3xl font-semibold tracking-[-0.04em] text-white sm:text-4xl">
              {te("One workspace. Choose your pace.")}
            </h2>
          </div>

          <NeonFrame accent="purple" className="mx-auto mt-10 max-w-4xl">
            <div className="relative grid gap-7 p-7 text-left sm:p-9 lg:grid-cols-[1fr_minmax(16rem,0.72fr)] lg:items-center">
              <div>
                <p className="max-w-xl text-sm leading-7 text-white/48">
                  {te(
                    "Use 2.0 General for free. Subscribe to 3.0 Flagship when you need deeper analysis.",
                  )}
                </p>
              </div>
              <Link className="block" href="/purchase">
                <span className="block rounded-[18px] bg-[linear-gradient(100deg,rgba(143,232,244,0.7),rgba(174,188,248,0.3)_52%,rgba(191,116,242,0.68))] p-px shadow-[0_10px_34px_rgba(150,190,255,0.1)]">
                  <FoilButton
                    className="h-14 w-full rounded-[17px] px-8 text-sm font-medium"
                    tone="aurora"
                  >
                    {te("View plans")}
                  </FoilButton>
                </span>
              </Link>
            </div>
          </NeonFrame>
        </section>

        <section className="border-t border-white/[0.055] pt-10 text-center">
          <p className="mx-auto max-w-xl text-xs leading-6 text-white/34">
            {te(
              "Built for reflection and risk understanding — not trade calls, execution, or investment advice.",
            )}
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-white/28">
            <Link className="hover:text-white/58" href="/about">
              {te("About")}
            </Link>
            <Link className="hover:text-white/58" href="/philosophy">
              {te("Philosophy")}
            </Link>
            <Link className="hover:text-white/58" href="/safety">
              {te("Scope & safety")}
            </Link>
            <Link className="hover:text-white/58" href="/terms">
              {te("Terms")}
            </Link>
            <Link className="hover:text-white/58" href="/privacy">
              {te("Privacy Policy")}
            </Link>
          </div>
        </section>
      </main>
    </UnauthShell>
  );
}
